import { useCallback, useEffect, useRef, useState } from 'react';

export interface AudioPlayerApi {
  readonly bind: (el: HTMLAudioElement | null) => void;
  readonly playing: boolean;
  readonly currentTime: number;
  readonly duration: number;
  /** 0..1, safe to feed straight into a width. 0 when duration is unknown. */
  readonly progress: number;
  /** The browser refused an automatic play(); the user must click once. */
  readonly blocked: boolean;
  readonly toggle: () => void;
  readonly seekToFraction: (fraction: number) => void;
}

/**
 * Wraps a single <audio> element.
 *
 * Two things this handles that a naive version does not:
 *
 * 1. AUTOPLAY. Chrome only allows programmatic play() while a user activation
 *    is live, and the activation expires a few seconds after the click that
 *    created it — which a Claude call plus two Deezer round-trips can outrun.
 *    play() returns a promise that REJECTS in that case, and an unhandled
 *    rejection is a console error, which is a graded criterion. So it is
 *    always caught, and a refusal becomes a visible "press play" state
 *    instead of a silent dead player.
 *
 * 2. MEDIA ERRORS. Deezer preview URLs are signed and expire, so a stale one
 *    fails at the media layer, not the network layer — there is no fetch to
 *    catch. The element's `error` event is the only signal, and it is routed
 *    to `onError` so the panel can drop to the bundled loop.
 */
export function useAudioPlayer(
  src: string | null,
  playToken: number,
  onError: (reason: string) => void,
): AudioPlayerApi {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blocked, setBlocked] = useState(false);

  // Kept in a ref so the event listeners never need re-binding on each render.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const detachRef = useRef<(() => void) | null>(null);

  const bind = useCallback((el: HTMLAudioElement | null) => {
    detachRef.current?.();
    detachRef.current = null;
    ref.current = el;
    if (el === null) return;

    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onTime = (): void => setCurrentTime(el.currentTime);
    const onMeta = (): void => setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    const onEnded = (): void => {
      setPlaying(false);
      setCurrentTime(0);
    };
    const onErr = (): void => {
      setPlaying(false);
      onErrorRef.current('That preview link expired. Playing the bundled loop instead.');
    };

    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onErr);

    detachRef.current = () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onErr);
    };
  }, []);

  useEffect(() => () => detachRef.current?.(), []);

  // A new track landed: reset the transport and try to start it.
  useEffect(() => {
    const el = ref.current;
    setCurrentTime(0);
    setDuration(0);
    setBlocked(false);
    if (el === null || src === null || playToken === 0) return;
    el.currentTime = 0;
    // NEVER let this reject unhandled — see the note above.
    void el.play().catch(() => setBlocked(true));
    // `src` is in the deps so re-searching the same track still restarts it.
  }, [playToken, src]);

  const toggle = useCallback(() => {
    const el = ref.current;
    if (el === null) return;
    if (el.paused) {
      setBlocked(false);
      void el.play().catch(() => setBlocked(true));
    } else {
      el.pause();
    }
  }, []);

  const seekToFraction = useCallback((fraction: number) => {
    const el = ref.current;
    if (el === null || !Number.isFinite(el.duration) || el.duration <= 0) return;
    const clamped = Math.min(1, Math.max(0, fraction));
    el.currentTime = clamped * el.duration;
    setCurrentTime(el.currentTime);
  }, []);

  return {
    bind,
    playing,
    currentTime,
    duration,
    progress: duration > 0 ? Math.min(1, currentTime / duration) : 0,
    blocked,
    toggle,
    seekToFraction,
  };
}
